import cx from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { MdSearch, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { TextInput } from './common/TextInput';
import { SearchSelect } from './common/SearchSelect/SearchSelect';
import AdSearchSelect, { AdSearchSelect_AdOption } from './common/SearchSelect/Ad_SearchSelect';
import AdVariation_SearchSelect, { AdVariationSearchSelect_AdVariationOption } from './common/SearchSelect/AdVariation_SearchSelect';
import Shop_SearchSelect from './common/SearchSelect/Shop_SearchSelect';
import { OrdersFilters } from '../../codegen/graphql';
import * as countryCodes from 'country-codes-list';
import { Select } from './common/Select';


export function OrderFiltersBlock({
  currentFilters,
  onFiltersChange,
}: {
  currentFilters: OrdersFilters;
  onFiltersChange: (
    changed: Partial<OrdersFilters>,
    allFilters: OrdersFilters,
  ) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState(currentFilters.search ?? '');

  const countryOptions = [
    { value: '', label: 'All Countries' },
    { value: 'FR', label: `${countryCodes.findOne('countryCode', 'FR')?.flag} France` },
    { value: 'IT', label: `${countryCodes.findOne('countryCode', 'IT')?.flag} Italy` },
    { value: 'ES', label: `${countryCodes.findOne('countryCode', 'ES')?.flag} Spain` },
    { value: 'BE', label: `${countryCodes.findOne('countryCode', 'BE')?.flag} Belgium` },
    { value: 'DE', label: `${countryCodes.findOne('countryCode', 'DE')?.flag} Germany` },
    { value: 'CH', label: `${countryCodes.findOne('countryCode', 'CH')?.flag} Switzerland` },
    { value: 'FI', label: `${countryCodes.findOne('countryCode', 'FI')?.flag} Finland` },
    { value: 'HR', label: `${countryCodes.findOne('countryCode', 'HR')?.flag} Croatia` },
    { value: 'PT', label: `${countryCodes.findOne('countryCode', 'PT')?.flag} Portugal` },
  ];

  const orderTypes = [
    { value: 'dropshipping', label: 'Dropshipping' },
    { value: 'inventory', label: 'Inventory' },
  ];

  const channelOptions = [
    { value: '', label: 'All Channels' },
    { value: 'aliexpress', label: 'AliExpress' },
    { value: 'ebay', label: 'eBay' },
    { value: 'temu', label: 'Temu' },
    { value: 'leboncoin', label: 'Leboncoin' },
    { value: 'direct-sale', label: 'Direct Sale' },
  ];

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const anyFiltersActive = useMemo(() => {
    return Object.values(currentFilters)
      .some(value => value !== undefined && value !== null);
  }, [currentFilters]);

  const handleFiltersChange = useCallback((changed: Partial<OrdersFilters>) => {
    onFiltersChange(changed, {
      ...currentFilters,
      ...changed,
    });
  }, [onFiltersChange, currentFilters]);

  return (
    <>
      <div className="relative mt-4 mb-6 flex justify-start items-center">
        <button
          type="button"
          onClick={toggleExpanded}
          className={cx(
            'mr-4 flex items-center text-gray-700 hover:text-gray-900 transition-colors',
          )}
        >
          {isExpanded
            ? <MdExpandLess size={20} color={anyFiltersActive ? 'blue' : 'gray'} />
            : <MdExpandMore size={20} color={anyFiltersActive ? 'blue' : 'gray'} />}
          <span className={cx(
            'font-medium',
            anyFiltersActive && 'text-blue-500 rounded-full',
          )}>More Filters</span>
        </button>
        <TextInput
          LeftAccessory={<MdSearch className="h-5 w-5 text-gray-400" />}
          type="text"
          placeholder="Search by customer name, address, phone, order ID, or tracking number..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          onBlur={() => handleFiltersChange({ search: searchText })}
          containerClassName="flex-1"
        />
      </div>

      {isExpanded && (
        <div className="mb-6 flex flex-wrap gap-4 items-end p-4 bg-gray-50 rounded-lg border">
          <TextInput
            label="Start Date"
            type="date"
            value={currentFilters.startDate ?? ''}
            onChange={(e) => handleFiltersChange({ startDate: e.target.value })}
          />
          <TextInput
            id="endDate"
            label="End Date"
            type="date"
            value={currentFilters.endDate ?? ''}
            onChange={(e) => handleFiltersChange({ endDate: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <SearchSelect<{ value: string; label: string }>
            loadOptions={(searchText) => Promise.resolve(countryOptions.map((option) => ({
              value: option.value,
              label: option.label,
            })).filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase())))}
            value={countryOptions.find((option) => option.value === currentFilters.countryCode) || null}
            onSelect={(option) => handleFiltersChange({ countryCode: option.value })}
            onClear={() => handleFiltersChange({ countryCode: undefined })}
            renderOption={(option, selectCallback) => (
              <div className="px-2 py-1" onClick={selectCallback}>{option.label}</div>
            )}
            renderSelected={(option) => option.label}
            label="Country"
            placeholder="Select a country"
          />

          <AdSearchSelect
            selectedAd={
              currentFilters.adId
                ? {
                  id: currentFilters.adId,
                  title: currentFilters.adId,
                } : null
            }
            onAdChange={(ad) => {
              handleFiltersChange({ adId: ad?.id ?? undefined });
            }}
          />

          <AdVariation_SearchSelect
            selectedAdVariation={currentFilters.variationId
              ? {
                id: currentFilters.variationId,
                variationTypeValues: [{ id: '__url__', value: currentFilters.variationId }],
              } : null
            }
            onAdVariationChange={(variation) => {
              handleFiltersChange({ variationId: variation?.id ?? undefined })
            }}
            adId={currentFilters.adId ?? undefined}
          />

          <Select
            label="Order Type"
            options={orderTypes}
            value={currentFilters.orderType ?? ''}
            onChange={(value) => handleFiltersChange({ orderType: value })}
          />

          <Select
            label="Channel"
            options={channelOptions}
            value={currentFilters.channel ?? ''}
            onChange={(value) => handleFiltersChange({ channel: value })}
          />

          <Shop_SearchSelect
            selectedShop={
              currentFilters.shopId
                ? {
                  id: currentFilters.shopId,
                  title: currentFilters.shopId,
                  shopType: '',
                  shopKey: '',
                }
                : null
            }
            onShopChange={(shop) => {
              handleFiltersChange({ shopId: shop?.id ?? undefined });
            }}
          />
        </div>
      )}
    </>
  );
}
